import React from 'react';
import { ProjetCalcule } from '../types';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';

interface MATOverviewProps {
  projet: ProjetCalcule;
}

const fmt = (n: number) => Math.round(n).toLocaleString('fr-FR');

const tendanceIcon = (tendance: 'amelioration' | 'stable' | 'degradation') => {
  switch (tendance) {
    case 'amelioration':
      return <ArrowTrendingUpIcon className="w-4 h-4 text-emerald-500" />;
    case 'degradation':
      return <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />;
    default:
      return <MinusIcon className="w-4 h-4 text-gray-400" />;
  }
};

const tendanceLabel = (tendance: 'amelioration' | 'stable' | 'degradation') => {
  switch (tendance) {
    case 'amelioration': return 'En amélioration';
    case 'degradation': return 'En dégradation';
    default: return 'Stable';
  }
};

const MATOverview: React.FC<MATOverviewProps> = ({ projet }) => {
  const mat = projet.mat;
  const isPositif = mat.margeATerminaison >= 0;

  // Sort lots by MAT contribution
  const lotsAvecMAT = projet.lotsCalcules
    .filter(l => l.cumulSituations > 0 || l.statut === 'termine')
    .sort((a, b) => a.mat.margeATerminaison - b.mat.margeATerminaison);

  const maxAbsMAT = Math.max(
    ...lotsAvecMAT.map(l => Math.abs(l.mat.margeATerminaison)),
    1
  );

  return (
    <section className="p-6 bg-white shadow rounded-xl mb-8">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Marge à Terminaison (MAT)</h2>
        <div className="flex items-center gap-1 ml-2">
          {tendanceIcon(mat.tendance)}
          <span className={`text-xs font-medium ${
            mat.tendance === 'amelioration' ? 'text-emerald-600' :
            mat.tendance === 'degradation' ? 'text-red-600' : 'text-gray-500'
          }`}>
            {tendanceLabel(mat.tendance)}
          </span>
        </div>
      </div>

      {/* KPIs MAT */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
          <div className="text-xs text-gray-500">Coût prévisionnel</div>
          <div className="text-lg font-semibold text-gray-900">{fmt(mat.coutPrevisionnel)} €</div>
        </div>
        <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
          <div className="text-xs text-gray-500">PV à terminaison</div>
          <div className="text-lg font-semibold text-gray-900">{fmt(mat.prixDeVenteTerminaison)} €</div>
        </div>
        <div className={`p-3 rounded-lg border ${isPositif ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
          <div className={`text-xs ${isPositif ? 'text-emerald-500' : 'text-red-500'}`}>MAT</div>
          <div className={`text-lg font-bold ${isPositif ? 'text-emerald-600' : 'text-red-600'}`}>
            {isPositif ? '+' : ''}{fmt(mat.margeATerminaison)} €
          </div>
          <div className={`text-xs font-medium ${isPositif ? 'text-emerald-500' : 'text-red-400'}`}>
            {mat.tauxMAT.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Barres par lot */}
      {lotsAvecMAT.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Contribution MAT par lot</h3>
          <div className="space-y-2">
            {lotsAvecMAT.map(lot => {
              const pct = (lot.mat.margeATerminaison / maxAbsMAT) * 100;
              const isLotPositif = lot.mat.margeATerminaison >= 0;
              return (
                <div key={lot.id} className="flex items-center gap-3">
                  <div className="w-48 text-sm text-gray-700 truncate flex-shrink-0">{lot.nom}</div>
                  <div className="flex-1 flex items-center">
                    <div className="w-full bg-gray-100 rounded-full h-3 relative overflow-hidden">
                      <div
                        className={`h-3 rounded-full ${isLotPositif ? 'bg-emerald-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(Math.abs(pct), 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 w-32 justify-end">
                    <span className={`text-sm font-medium ${isLotPositif ? 'text-emerald-600' : 'text-red-600'}`}>
                      {isLotPositif ? '+' : ''}{fmt(lot.mat.margeATerminaison)} €
                    </span>
                    {tendanceIcon(lot.mat.tendance)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default MATOverview;
